import { Component, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { FundPerformanceBackendService } from 'src/app/domain/fund-performance/service/fund-performance-backend.service';
import { ModalButtons, SpinnerProps } from 'src/app/domain/shared/component/modal-container/modal-container.component';
import { InvestorContributionService } from '../../../../../investors-and-net-irr/service/investor-contribution.service';
import { InvestorContributionUtil } from '../../../../../investors-and-net-irr/dto/InvestorContribution';
import { ExcelTemplateName } from '../../../../../excel/interfaces';
import { ExcelService } from '../../../../../excel';
import { FundPerformance } from '../../../../dto/FundPerformance';
import { ButtonProps } from '../../../../../shared/component/buttons/button.component';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { ValueUtils } from "../../../../../../core/service/value-utils/value-utils.service";
import { NetIrrWrapper } from '../../fund-performance-card/fund-performance-card.component';
import { FpSelected } from '../../fp-value-creation/fp-value-creation.component';
import { MAX_COMMENT_LENGTH_STANDARD } from 'src/app/core/pojo/common-const';
import { InputErrorClass } from "../../../../../shared/component/input/input-component/input.component";
import { FinCalc } from "../../../../../../services/financial-calculations/financial-calculations.service";
import { FpFormService } from "./fp-form-controls-service/fp-form.service";
import { FundPerformanceFormGroup } from "./fp-form-controls-service/fp-form-pojo";

@Component({
  selector: 'app-modal-fund-performance-update',
  templateUrl: './modal-fund-performance-update.component.html',
  styleUrls: ['./modal-fund-performance-update.component.scss'],
})
export class ModalFundPerformanceUpdateComponent implements OnDestroy, OnChanges {
  private _destroyStream: Subject<void> = new Subject<void>();
  @Input() selected: FpSelected;
  @Input() fundPerformanceList: FundPerformance[];
  @Input() isFromCopyPeriodModal: boolean;
  @Output() isConfirmedUpdate = new EventEmitter<boolean>();
  @Output() fundPerfSetter = new EventEmitter<FundPerformance>();
  @Output() deleteFundPerformance = new EventEmitter<FundPerformance>();

  header = 'Fund performance';
  buttonProps: ButtonProps = { size: 'xsmall', type: 'accent', text: 'Export', showIcon: 'left', iconName: 'excel.svg' };
  showExpensesPredictionModal = false;
  hintOnBlockedPeriod: string;
  showExpensesButton = false;
  fpGroup: FundPerformanceFormGroup;
  errorClass = new AdditionalClasses();
  updatedFpList: FundPerformance[] = [];
  MAX_COMMENT_LEN = MAX_COMMENT_LENGTH_STANDARD;

  modalButtons: ModalButtons = {
    close: true,
    dismiss: 'Cancel',
    apply: 'Apply',
    lowerLeft: 'Delete',
    lowerLeftIcon: 'delete.svg',
    lowerLeftClass: 'light-red',
  };

  spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  constructor(
    private fpService: FundPerformanceBackendService,
    private icService: InvestorContributionService,
    private excelService: ExcelService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selected && this.selected && this.selected.fp) {
      this.cloneFundPerf(this.selected.fp);
      this.countAll();
      this.getHeaderWithPeriod();
      if (this.selected.fp.blocked) {
        this.hintOnBlockedPeriod = 'Please unlock the Period';
      } else {
        this.hintOnBlockedPeriod = '';
      }
      if (this.selected.fp.carryInterestPercent) {
        this.countCarryInterest(this.selected.fp.carryInterestPercent);
      }
    }

    if (changes.fundPerformanceList && this.fundPerformanceList) {
      this.checkPreviousPeriods();
    }

    if (changes.isFromCopyPeriodModal && this.isFromCopyPeriodModal) {
      this.checkPreviousPeriods();
      this.showExpensesPredictionModal = this.showExpensesButton;
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  cloneFundPerf(f: FundPerformance): void {
    this.createForm(f);
    this.countAll();
  }

  async confirmUpdateFundPerformance(): Promise<void> {
    const { netIrr, netIrrAfterCarry } = this.fpGroup.getRawValue();
    this.fpGroup.patchValue({
      mgmFees: this.fpGroup.value.isTotalExpensesPositive ? 0 : -Math.abs(this.fpGroup.value.mgmFees),
      totalExpenses: this.fpGroup.value.totalExpenses !== null && this.fpGroup.value.totalExpenses !== undefined ?
        this.fpGroup.value.totalExpenses : null,
      aggregatedOrgExpenses: this.fpGroup.value.isTotalExpensesPositive ? 0 : -Math.abs(this.fpGroup.value.aggregatedOrgExpenses),
      otherExpenses: this.fpGroup.value.isTotalExpensesPositive ? 0 : this.fpGroup.getRawValue().otherExpenses,
      netIrr: netIrr ? netIrr / 100 : null,
      netIrrAfterCarry: netIrrAfterCarry ? netIrrAfterCarry / 100 : null,
      expectedOpEx: this.fpGroup.value.expectedOpEx,
      carryInterestPercent: -Math.abs(this.fpGroup.value.carryInterestPercent),
      carryInterest: -Math.abs(this.fpGroup.value.carryInterest),
    });
    const f = await this.fpService.update(this.fpGroup.value as FundPerformance);
    if (f) {
      this.fundPerfSetter.emit(f);
      this.close(true);
    }
  }

  emitDeleteFundPerf(): void {
    this.deleteFundPerformance.emit(this.selected.fp);
  }

  close(isUpdated: boolean): void {
    this.isConfirmedUpdate.emit(isUpdated);
  }

  showExpensesPrediction(): void {
    this.showExpensesPredictionModal = !this.showExpensesPredictionModal;
  }

  checkApplyActivated(): boolean {
    const { partnersCapital } = this.fpGroup.getRawValue();
    return partnersCapital >= 0;
  }

  setExpenses(expenses: {
    totalExpenses: number;
    mgmFees: number;
    aggregatedOrgExpenses: number;
  }): void {
    this.fpGroup.patchValue({
      mgmFees: -Math.abs(expenses.mgmFees),
      totalExpenses: expenses.totalExpenses,
      aggregatedOrgExpenses: -Math.abs(expenses.aggregatedOrgExpenses),
    });
    this.countAll();
    this.showExpensesPredictionModal = false;
    this.calculateNewIrr();
  }

  /**
   * 'totalExpenses' will change sign automatically
   * If 'Unlock' button is clicked, errors should be cleared.
   * If 'Lock' button is clicked, 'Other expenses' should be recalculated.
   *
   * VERY IMPORTANT!!!
   * In input by click on the suffixImg is (mousedown) event is happening, which is not
   * causes (blur) event, thats why we need to call 'calculateNewIrr()' here additionally
   */
  toggleIsTotalExpensesPositive(): void {
    this.fpGroup.controls.isTotalExpensesPositive.patchValue(!this.fpGroup.value.isTotalExpensesPositive);
    this.fpGroup.controls.totalExpenses.patchValue(-this.fpGroup.value.totalExpenses);
    if (!this.fpGroup.value.isTotalExpensesPositive) {
      this.countOtherExpenses();
    }
    this.countNetGainBeforeCarry();
    this.countPartnersCapital();
    this.calculateNewIrr(); // read description
    this.updateTotalExpensesIcon();
  }

  countTvpiBeforeCarry(): void {
    this.fpGroup.patchValue({ tvpiBeforeCarry: null });
    if (this.fpGroup.value.contribution && this.fpGroup.value.totalExpenses !== null) {
      this.fpGroup.patchValue({
        tvpiBeforeCarry:
          ((this.nullToZero(this.fpGroup.getRawValue().partnersCapital) +
            Math.abs(this.nullToZero(this.fpGroup.value.distribution)) +
            Math.abs(this.nullToZero(this.fpGroup.value.carryDistribution))) /
            this.nullToZero(this.fpGroup.value.contribution)),
      });
    }
  }

  countTvpiAfterCarry(): void {
    this.fpGroup.patchValue({ tvpiAfterCarry: null });
    if (this.fpGroup.value.contribution && this.fpGroup.value.totalExpenses !== null) {
      this.fpGroup.patchValue({
        tvpiAfterCarry:
          ((this.nullToZero(this.fpGroup.getRawValue().netGainAfterCarry) +
            this.nullToZero(Number(this.fpGroup.value.contribution))) /
            this.nullToZero(Number(this.fpGroup.value.contribution))),
      });
    }
  }

  handleValueChange(field: keyof FundPerformance, numberValue: number, negate: boolean = false): void {
    const value = numberValue.toString();
    if (value && value !== '-') {
      const tempValue = Math.abs(Number(value));
      this.fpGroup.patchValue({ [field]: negate ? -tempValue : tempValue }, { emitEvent: false });
    } else {
      this.fpGroup.patchValue({ [field]: null }, { emitEvent: false });
    }
    this.countAll();
  }

  aggregatedLossChange(numberValue: number): void {
    if (numberValue) {
      this.handleValueChange('totalExpenses', numberValue, !this.fpGroup.value.isTotalExpensesPositive);
    } else {
      this.fpGroup.patchValue({
        totalExpenses: null,
        mgmFees: this.fpGroup.value.mgmFees || null,
        aggregatedOrgExpenses: this.fpGroup.value.aggregatedOrgExpenses || null,
        otherExpenses: this.fpGroup.value.otherExpenses || null,
      }, { emitEvent: false });
      this.countAll();
    }
  }

  /**
   * Calculates the suffix text based on the provided type.
   * If the type is 'contribution', it calculates the percentage of contribution to total commitment.
   * If the type is 'totalExpenses', it calculates the percentage of total expenses to total commitment.
   *
   * @param type The type of calculation ('contribution' or 'totalExpenses').
   * @returns The suffix text representing the percentage, formatted accordingly,
   * or an empty string if the denominator is zero.
   */
  calculateSuffixText(type: 'contribution' | 'totalExpenses'): string {
    const totalCommitment = this.fpGroup.value.totalCommitment;
    if (type === 'contribution') {
      const contribution = this.fpGroup.value.contribution;
      if (!contribution) {
        return '';
      }
      return totalCommitment ? `${(100 * contribution / totalCommitment).toFixed(2)}%` : '';
    } if (type === 'totalExpenses') {
      const totalExpenses = this.fpGroup.value.totalExpenses;
      if (!totalExpenses) {
        return '';
      }
      return totalCommitment ? `${(100 * Math.abs(totalExpenses) / totalCommitment).toFixed(2)}%` : '';
    }
    return '';
  }


  countCarryInterestPercent(carryInt: number): void {
    if (!carryInt) {
      this.setCarryInterestNull();
    } else {
      this.fpGroup.patchValue({ carryInterest: -Math.abs(this.fpGroup.controls.carryInterest.value) }, { emitEvent: false });
      if (this.fpGroup.getRawValue().netGainBeforeCarry) {
        const numberValue = (carryInt / this.fpGroup.getRawValue().netGainBeforeCarry * 100);
        const decimalCount = numberValue.toString().split('.')[1] || 0;
        const numberOfDigitsAfterComma = (numberValue && decimalCount) ? 6 : 0;
        this.fpGroup.patchValue({ carryInterestPercent: -Math.abs(Number(numberValue.toFixed(numberOfDigitsAfterComma))) }, { emitEvent: false });
      }
    }
    this.countAfterCarry();
  }

  countCarryInterest(carryPcnt: number): void {
    if (!carryPcnt) {
      this.setCarryInterestNull();
    } else {
      this.fpGroup.patchValue({ carryInterestPercent: -Math.abs(this.fpGroup.controls.carryInterestPercent.value) },
        { emitEvent: false });
      this.fpGroup.patchValue({ carryInterest: -(Math.abs(carryPcnt) / 100) * this.fpGroup.getRawValue().netGainBeforeCarry },
        { emitEvent: false });
    }
    this.countAfterCarry();
  }

  setCarryInterestNull(): void {
    this.fpGroup.patchValue({ carryInterest: null }, { emitEvent: false });
    this.fpGroup.patchValue({ carryInterestPercent: null }, { emitEvent: false });
  }

  countAll(): void {
    this.countPortfolioGain();
    this.countOtherExpenses();
    this.countPartnersCapital();
    this.countBeforeCarry();
    this.countAfterCarry();
    this.countUnrealizedCarry();
    this.setErrorClasses();
  };

  countUnrealizedCarry(): void {
    const fp = this.fpGroup.value;
    this.fpGroup.patchValue({ unrealizedCarry: Number(fp.carryInterest) - Number(fp.carryDistribution) });
  }

  countBeforeCarry(): void {
    this.countNetGainBeforeCarry();
    this.countNavAndDistributionBeforeCarry();
    this.countTvpiBeforeCarry();
  }

  countAfterCarry(): void {
    this.countNetGainAfterCarry();
    this.countNavAfterCarry();
    this.countNavAndDistributionAfterCarry();
    this.countTvpiAfterCarry();
    this.countUnrealizedCarry();
  }


  countOtherExpenses(): void {
    const conditionToShowOtherExpenses = (this.fpGroup.value.mgmFees || this.fpGroup.value.aggregatedOrgExpenses);
    if (this.fpGroup.value.totalExpenses) {
      this.fpGroup.patchValue({
        otherExpenses: conditionToShowOtherExpenses ? this.nullToZero(this.fpGroup.value.totalExpenses) -
          this.nullToZero(this.fpGroup.value.mgmFees) -
          this.nullToZero(this.fpGroup.value.aggregatedOrgExpenses) : null,
      });
    } else {
      this.fpGroup.patchValue({ otherExpenses: null });
    }
  }

  countNetGainBeforeCarry(): void {
    if (!this.fpGroup.value.contribution || ValueUtils.isValueAbsent(this.fpGroup.value.totalExpenses)) {
      this.fpGroup.patchValue({ netGainBeforeCarry: null });
    } else {
      const formData = this.fpGroup.getRawValue();
      this.fpGroup.controls.netGainBeforeCarry.patchValue(
        this.nullToZero(formData.portfolioGain) + this.nullToZero(formData.totalExpenses),
      );
      if (this.fpGroup.getRawValue().netGainBeforeCarry === 0) {
        this.fpGroup.patchValue({ netGainBeforeCarry: null });
      }
    }
  }

  countNetGainAfterCarry(): void {
    if (!this.fpGroup.value.contribution || ValueUtils.isValueAbsent(this.fpGroup.value.totalExpenses)) {
      this.fpGroup.patchValue({ netGainAfterCarry: null });
    } else {
      this.fpGroup.patchValue({
        netGainAfterCarry:
          Math.floor(this.nullToZero(this.fpGroup.getRawValue().netGainBeforeCarry) -
            Math.abs(this.nullToZero(this.fpGroup.value.carryInterest))),
      });
      if (this.fpGroup.getRawValue().netGainAfterCarry === 0) {
        this.fpGroup.patchValue({ netGainAfterCarry: null });
      }
    }
  }

  countPortfolioGain(): void {
    this.fpGroup.patchValue({
      portfolioGain:
        Math.round(this.fpGroup.value.totalFmv) -
        Math.round(this.fpGroup.value.totalCost),
    });
    if (this.fpGroup.getRawValue().portfolioGain === 0) {
      this.fpGroup.patchValue({ portfolioGain: null });
    }
  }

  countPartnersCapital(): void {
    this.fpGroup.patchValue({
      partnersCapital:
        FinCalc.navWithGain(this.fpGroup.getRawValue().contribution,
          this.fpGroup.getRawValue().distribution,
          this.fpGroup.getRawValue().carryDistribution,
          this.fpGroup.getRawValue().totalExpenses,
          this.fpGroup.getRawValue().portfolioGain),
    });
    if (this.fpGroup.getRawValue().partnersCapital === 0) {
      this.fpGroup.patchValue({ partnersCapital: null });
    }
  }

  async exportCalculationIrr(): Promise<void> {
    const ics = await firstValueFrom(this.icService.getByFund(this.selected.org.selectedFund.uuid));
    const fp: FundPerformance = this.fpGroup.getRawValue();
    const fundPerformance: FundPerformance = { ...fp };
    fundPerformance.navBeforeCarry = this.fpGroup.getRawValue().partnersCapital;
    const arrayFP = [];
    arrayFP.push(fundPerformance);
    const fundNetIrr = new InvestorContributionUtil().prepareFundNetIrrData(arrayFP, ics);
    const searchName = `Till ${this.selected.fp.period.toString()}`;
    const icCurrentPeriodIndex = fundNetIrr.findIndex((icPeriod) => icPeriod.name.startsWith(searchName));
    const fileName = this.selected.period.toString() + '_' + this.selected.org.selectedFund.name + '_net_irr.xlsx';
    const wrapper: NetIrrWrapper = {
      icPeriods: fundNetIrr.slice(icCurrentPeriodIndex),
      isAfterCarry: true,
    };
    this.excelService.exportByTemplate(wrapper, fileName, ExcelTemplateName.investorContributionTemplate);
  }

  countNavAfterCarry(): void {
    if (!this.fpGroup.value.contribution || ValueUtils.isValueAbsent(this.fpGroup.value.totalExpenses)) {
      this.fpGroup.patchValue({ navAfterCarry: null });
    } else {
      this.fpGroup.patchValue({
        navAfterCarry: this.nullToZero(this.fpGroup.getRawValue().partnersCapital) +
          (this.nullToZero(this.fpGroup.value.carryInterest) -
            this.nullToZero(this.fpGroup.value.carryDistribution)),
      });
    }
  }

  countNavAndDistributionBeforeCarry(): void {
    this.fpGroup.patchValue({
      navAndDistributionBeforeCarry: this.nullToZero(this.fpGroup.getRawValue().partnersCapital) - this.nullToZero(this.fpGroup.value.carryDistribution) - this.nullToZero(this.fpGroup.value.distribution),
    });
  }

  countNavAndDistributionAfterCarry(): void {
    this.fpGroup.patchValue({
      navAndDistributionAfterCarry: this.nullToZero(this.fpGroup.getRawValue().navAfterCarry) - this.nullToZero(this.fpGroup.value.distribution),
    });
  }

  changeIsVisibleValue(): void {
    const value = this.fpGroup.value.isVisible;
    this.fpGroup.patchValue({ isVisible: !value });
  }

  setTvpiIcon(): void {
    const tvpiBeforeCarry = this.fpGroup.controls.tvpiBeforeCarry.value;
    const netIrrBeforeCarry = this.fpGroup.controls.netIrr.value;
    const ifTvpiOrIrrBeforeCarryPresent = tvpiBeforeCarry && netIrrBeforeCarry;
    const errorBeforeCarry = (tvpiBeforeCarry < 1 && netIrrBeforeCarry > 0) || (tvpiBeforeCarry > 1 && netIrrBeforeCarry < 0);
    const iconBeforeCarry = errorBeforeCarry ? 'exclamation.svg' : null;
    this.fpGroup.patchValue({ tvpiBeforeCarryIcon: ifTvpiOrIrrBeforeCarryPresent ? iconBeforeCarry : null });

    const tvpiAfterCarry = this.fpGroup.controls.tvpiAfterCarry.value;
    const netIrrAfterCarry = this.fpGroup.controls.netIrrAfterCarry.value;
    const ifTvpiOrIrrAfterCarryPresent = tvpiAfterCarry && netIrrAfterCarry;
    const errorAfterCarry = (tvpiAfterCarry < 1 && netIrrAfterCarry > 0) || (tvpiAfterCarry > 1 && netIrrAfterCarry < 0);
    const iconAfterCarry = errorAfterCarry ? 'exclamation.svg' : null;
    this.fpGroup.patchValue({ tvpiAfterCarryIcon: ifTvpiOrIrrAfterCarryPresent ? iconAfterCarry : null });
  }

  async calculateNewIrr(): Promise<void> {
    const fp = { ...this.fpGroup.getRawValue() } as FundPerformance;
    fp.totalExpenses = fp.totalExpenses !== null && fp.totalExpenses !== undefined ? fp.totalExpenses : null;
    fp.navBeforeCarry = this.fpGroup.getRawValue().partnersCapital;
    const newCalculatedIrr = await firstValueFrom(this.fpService.calculateIrr(fp, this.selected.org.selectedFund.uuid));
    this.fpGroup.patchValue({
      irrCalculated: ValueUtils.isValuePresent(newCalculatedIrr.irr) ? (newCalculatedIrr.irr * 100) : newCalculatedIrr.irr,
      irrCalculatedAfterCarry: ValueUtils.isValuePresent(newCalculatedIrr.irrAfterCarry) ? (newCalculatedIrr.irrAfterCarry * 100)
        : newCalculatedIrr.irrAfterCarry,
    });
    this.selected.fp.isIrrCanBeCalculated = newCalculatedIrr.irrCanBeCalculated;
  }

  private nullToZero(value: number): number {
    return value ? value : 0;
  }

  private getHeaderWithPeriod(): void {
    this.header = 'Fund performance as of ' + this.selected.fp?.period.toString();
  }

  private checkPreviousPeriods(): void {
    if (!this.selected || !this.selected.fp) {
      return;
    }
    this.updatedFpList = cloneDeep(this.fundPerformanceList.filter((f) => f.totalExpenses));
    const tempList = this.updatedFpList.filter((f) => f.period < this.selected.fp.period);
    this.showExpensesButton = tempList.length > 1;
  }

  private createForm(fp: FundPerformance): void {
    this.fpGroup = FpFormService.fillFpForm(fp);
    this.updateTotalExpensesIcon();
    this.setTvpiIcon();
    this.setOnValueChangeActions();
  }

  private setOnValueChangeActions(): void {
    this.fpGroup.controls.contribution.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        this.countAll();
        this.calculateNewIrr();
      });

    this.fpGroup.controls.totalCommitment.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        this.handleValueChange('totalCommitment', this.fpGroup.controls.totalCommitment.value, false);
      });

    this.fpGroup.controls.aggregatedOrgExpenses.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        this.handleValueChange('aggregatedOrgExpenses', this.fpGroup.controls.aggregatedOrgExpenses.value, true);
        this.updateTotalExpensesIcon();
        this.setTvpiIcon();
      });

    this.fpGroup.controls.mgmFees.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        this.handleValueChange('mgmFees', this.fpGroup.controls.mgmFees.value, true);
        this.updateTotalExpensesIcon();
        this.setTvpiIcon();
      });

    this.fpGroup.controls.netIrr.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => this.setTvpiIcon());

    this.fpGroup.controls.netIrrAfterCarry.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => this.setTvpiIcon());

    this.fpGroup.controls.totalExpenses.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this._destroyStream),
      )
      .subscribe(() => {
        this.aggregatedLossChange(this.fpGroup.controls.totalExpenses.value);
        this.countCarryInterest(this.fpGroup.controls.carryInterestPercent.value);
        this.calculateNewIrr();
      });
    this.fpGroup.controls.carryInterest.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this._destroyStream),
      )
      .subscribe(() => {
        this.countCarryInterestPercent(this.fpGroup.controls.carryInterest.value);
        this.calculateNewIrr();
        this.resetManualIRR();
      });
    this.fpGroup.controls.carryInterestPercent.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this._destroyStream),
      )
      .subscribe(() => {
        this.countCarryInterest(this.fpGroup.controls.carryInterestPercent.value);
        this.calculateNewIrr();
      });
    this.fpGroup.controls.distribution.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this._destroyStream),
      )
      .subscribe(() => {
        this.handleValueChange('distribution', this.fpGroup.controls.distribution.value, true);
        this.calculateNewIrr();
      });
    this.fpGroup.controls.carryDistribution.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this._destroyStream),
      )
      .subscribe(() => {
        this.handleValueChange('carryDistribution', this.fpGroup.controls.carryDistribution.value, true);
      });
  }

  private resetManualIRR(): void {
    if (this.fpGroup.controls.carryInterest.value) {
      return;
    }
    this.fpGroup.controls.netIrrAfterCarry.patchValue(null);
  }

  private setErrorClasses(): void {
    // Form values
    const contribution = Number(this.fpGroup.controls.contribution.value);
    const totalCommitment = Number(this.fpGroup.controls.totalCommitment.value);
    const partnersCapital = Number(this.fpGroup.controls.partnersCapital.getRawValue());
    // Financial requirements which should be applied
    const commitmentGraterThanContribution = totalCommitment >= contribution;
    const partnersCapitalPositive = partnersCapital >= 0 || partnersCapital === null;
    // Setting of error classes
    this.errorClass.contribution = (commitmentGraterThanContribution && partnersCapitalPositive) ? '' : 'error';
    this.errorClass.totalCommitment = (commitmentGraterThanContribution) ? '' : 'error';
    this.errorClass.totalExpenses = partnersCapitalPositive ? '' : 'error';
    this.errorClass.distribution = partnersCapitalPositive ? '' : 'error';
    this.errorClass.partnersCapital = partnersCapitalPositive ? 'disabled' : 'error-for-disabled';
  }

  private updateTotalExpensesIcon(): void {
    this.fpGroup.patchValue({ totalExpIcon: this.getTotalExpIconAndToolTip().icon });
    this.fpGroup.patchValue({ totalExpToolTip: this.getTotalExpIconAndToolTip().toolTip });
  }

  private getTotalExpIconAndToolTip(): IconAndToolTip {
    const isPositive = this.fpGroup.controls.isTotalExpensesPositive.value;
    return {
      icon: isPositive ? 'lock-closed-with-minus.svg;icon-size-1p25' : 'lock-closed-with-plus.svg;icon-size-1p25',
      toolTip: isPositive ? 'Switch to negative number' : 'Switch to positive number',
    };
  }
}

export class AdditionalClasses {
  totalCommitment: InputErrorClass = '';
  contribution: InputErrorClass = '';
  totalExpenses: InputErrorClass = '';
  distribution: InputErrorClass = '';
  carryDistribution: InputErrorClass = '';
  unrealizedCarry: InputErrorClass = '';
  partnersCapital: InputErrorClass = '';
}

interface IconAndToolTip {
  icon: string;
  toolTip?: string;
}
